import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router';

import {initFirebaseAnalytics} from '@/shared/lib/firebase';
import {enableRollbarReplay} from '@/shared/lib/rollbar';
import {
  CookieConsent,
  cookieConsentService,
} from '@/shared/services/cookieConsent';

import {INITIAL_MODAL_PREFERENCES} from '../consts';
import {useFacebookPixel} from '../hooks/useFacebookPixel';
import {useTrackScreenToFirebaseAnalytics} from '../hooks/useTrackScreenToFirebaseAnalytics';
import {getCookiePreferences} from '../lib/getCookiePreferences';
import {isCookiePreferencesChanged} from '../lib/isCookiePreferencesChanged';
import {subscribeCookiePreferences} from '../lib/subscribeCookiePreferences';
import {isCookieBannerSuppressedPath} from '../lib/suppressedPaths';
import {CookiePreferences} from '../types';

type CookieConsentModalOpenMode = 'consent' | 'preferences';

type PreferencesModalState = {
  isForcedOpen: boolean;
  initialPreferences: CookiePreferences;
};

type UseCookieConsent = {
  isOpen: boolean;
  openMode: CookieConsentModalOpenMode;
  initialPreferences: CookiePreferences;
  onDecision: (consent: CookieConsent) => void;
  onClosePreferencesMode: () => void;
};

const INITIAL_PREFERENCES_MODAL_STATE: PreferencesModalState = {
  isForcedOpen: false,
  initialPreferences: INITIAL_MODAL_PREFERENCES,
};

export function useCookieConsent(): UseCookieConsent {
  const cypressEnv = typeof window !== 'undefined' && Boolean(window.Cypress);
  const [showModal, setShowModal] = useState(false);
  const [isMarketingAllowed, setIsMarketingAllowed] = useState(false);
  const [preferencesModalState, setPreferencesModalState] =
    useState<PreferencesModalState>(INITIAL_PREFERENCES_MODAL_STATE);

  const snapshotBeforeOpenRef = useRef<CookieConsent | null>(null);

  const {pathname} = useLocation();
  const isCookieBannerSuppressed = isCookieBannerSuppressedPath(pathname);

  useFacebookPixel({enabled: isMarketingAllowed});
  useTrackScreenToFirebaseAnalytics({
    skipInit: cypressEnv || !isMarketingAllowed,
  });

  useEffect(() => {
    const storedConsent = cookieConsentService.initFromStorage();
    setShowModal(!storedConsent);

    let cancelled = false;

    (async () => {
      const consent = await cookieConsentService.waitForDecision();
      if (cancelled) {
        return;
      }

      if (consent.categories.statistics) {
        enableRollbarReplay();
      }

      if (consent.categories.marketing && !cypressEnv) {
        setIsMarketingAllowed(true);
        initFirebaseAnalytics();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cypressEnv]);

  const openPreferencesMode = useCallback(
    (storedConsent: CookieConsent | null) => {
      snapshotBeforeOpenRef.current = storedConsent;

      setPreferencesModalState({
        isForcedOpen: true,
        initialPreferences: getCookiePreferences(storedConsent),
      });
    },
    []
  );

  const closePreferencesMode = useCallback(() => {
    snapshotBeforeOpenRef.current = null;
    setPreferencesModalState(INITIAL_PREFERENCES_MODAL_STATE);
  }, []);

  useEffect(() => {
    return subscribeCookiePreferences(() => {
      const storedConsent = cookieConsentService.readFromStorage();
      openPreferencesMode(storedConsent);
    });
  }, [openPreferencesMode]);

  const openMode: CookieConsentModalOpenMode =
    preferencesModalState.isForcedOpen ? 'preferences' : 'consent';

  const handleCookieDecision = useCallback(
    (consent: CookieConsent) => {
      cookieConsentService.saveDecision(consent);
      setShowModal(false);

      if (openMode !== 'preferences') {
        return;
      }

      const previousPreferences = getCookiePreferences(
        snapshotBeforeOpenRef.current
      );
      const nextPreferences = getCookiePreferences(consent);

      const shouldReload = isCookiePreferencesChanged(
        previousPreferences,
        nextPreferences
      );

      closePreferencesMode();

      if (shouldReload && typeof window !== 'undefined') {
        window.location.reload();
      }
    },
    [openMode, closePreferencesMode]
  );

  return {
    isOpen:
      (showModal && !isCookieBannerSuppressed) ||
      preferencesModalState.isForcedOpen,
    openMode,
    initialPreferences: preferencesModalState.initialPreferences,
    onDecision: handleCookieDecision,
    onClosePreferencesMode: closePreferencesMode,
  };
}
